import type { CSSProperties } from "react";
import type {
  BrandingBackground,
  BrandingBackgroundMode,
  BrandingConfig,
  BrandingWatermark,
  HomeLogoVariant,
} from "@/services/api/agentic-v2/types";

export function readBrandingLogoUrls(
  branding: BrandingConfig | null | undefined,
): { dark: string | null; light: string | null } {
  if (!branding) return { dark: null, light: null };
  return {
    dark: branding.logo_url_dark ?? branding.logo_url ?? null,
    light: branding.logo_url_light ?? null,
  };
}

export function hasUploadedBrandingLogo(
  branding: BrandingConfig | null | undefined,
): boolean {
  const { dark, light } = readBrandingLogoUrls(branding);
  return Boolean(dark || light);
}

/** Logo shown in settings previews — light variant falls back to dark like runtime. */
export function resolveBrandingLogoPreviewUrl(
  branding: BrandingConfig | null | undefined,
  variant: HomeLogoVariant,
): string | null {
  const { dark, light } = readBrandingLogoUrls(branding);
  if (variant === "dark") return dark;
  return light ?? dark;
}

export function readBackgroundImageUrls(
  branding: BrandingConfig | null | undefined,
): { dark: string | null; light: string | null } {
  const background = branding?.background;
  return {
    dark: background?.image_url_dark ?? null,
    light: background?.image_url_light ?? null,
  };
}

function normalizeHexColor(color: string): string {
  return color.trim().toLowerCase();
}

/** True when light and dark background values were explicitly set to differ. */
export function usesSeparateBackgroundByAppearance(
  background: BrandingBackground | null | undefined,
): boolean {
  if (!background) return false;

  if (background.mode === "color") {
    const light = background.color_light;
    const dark = background.color_dark;
    return Boolean(
      light && dark && normalizeHexColor(light) !== normalizeHexColor(dark),
    );
  }

  if (background.mode === "image") {
    const light = background.image_url_light;
    const dark = background.image_url_dark;
    return Boolean(light && dark && light !== dark);
  }

  return false;
}

export function readUnifiedBackgroundColor(
  background: BrandingBackground | null | undefined,
): string {
  return background?.color_light ?? background?.color_dark ?? "#1e293b";
}

export function resolveBackgroundColor(
  background: BrandingBackground | null | undefined,
  isDark: boolean,
): string | null {
  if (!background || background.mode !== "color") return null;

  const light = background.color_light;
  const dark = background.color_dark;

  if (!usesSeparateBackgroundByAppearance(background)) {
    return light ?? dark ?? null;
  }

  return isDark ? (dark ?? light ?? null) : (light ?? dark ?? null);
}

export function resolveBackgroundImageUrl(
  background: BrandingBackground | null | undefined,
  isDark: boolean,
): string | null {
  if (!background || background.mode !== "image") return null;

  const light = background.image_url_light;
  const dark = background.image_url_dark;

  if (!usesSeparateBackgroundByAppearance(background)) {
    return dark ?? light ?? null;
  }

  return isDark ? (dark ?? light ?? null) : (light ?? dark ?? null);
}

/** Image mode without a stored asset keeps the workspace theme wallpaper (upload pending). */
export function resolveDesktopWallpaperMode(
  background: BrandingBackground | null | undefined,
  isDark: boolean,
): BrandingBackgroundMode {
  const mode = background?.mode ?? "theme";
  if (mode === "image" && !resolveBackgroundImageUrl(background, isDark)) {
    return "theme";
  }
  return mode;
}

/** macOS Fill Screen equivalent — full bleed, crop edges, anchor top-left for branded art. */
export const BRANDING_BACKGROUND_IMAGE_SIZE = "cover" as const;
export const BRANDING_BACKGROUND_IMAGE_POSITION = "top left" as const;

export function resolveBrandingBackgroundImageStyle(
  imageSrc: string,
): CSSProperties {
  return {
    backgroundImage: `url(${imageSrc})`,
    backgroundSize: BRANDING_BACKGROUND_IMAGE_SIZE,
    backgroundPosition: BRANDING_BACKGROUND_IMAGE_POSITION,
  };
}

export function hasSeparateLightLogo(
  branding: BrandingConfig | null | undefined,
): boolean {
  return Boolean(branding?.logo_url_light);
}

export function defaultBackground(): BrandingBackground {
  return { mode: "theme" };
}

export function defaultBrandingConfig(): BrandingConfig {
  return {
    show_company_name: true,
    background: defaultBackground(),
    watermark: { enabled: false, position: "bottom_right", opacity: 100 },
  };
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return false;
}

function diffNestedRecord<T extends Record<string, unknown>>(
  base: T | null | undefined,
  draft: T | null | undefined,
): Partial<T> | undefined {
  const baseRecord = base ?? ({} as T);
  const draftRecord = draft ?? ({} as T);
  const diff: Partial<T> = {};
  const keys = new Set([...Object.keys(baseRecord), ...Object.keys(draftRecord)]);

  for (const key of keys) {
    const baseValue = baseRecord[key as keyof T];
    const draftValue = draftRecord[key as keyof T];
    if (!valuesEqual(baseValue, draftValue)) {
      diff[key as keyof T] = draftValue as T[keyof T];
    }
  }

  return Object.keys(diff).length > 0 ? diff : undefined;
}

/** Fields in draft that differ from company base — for unit-level override storage. */
export function diffBrandingOverride(
  companyBase: BrandingConfig,
  draft: BrandingConfig,
): Partial<BrandingConfig> {
  const override: Partial<BrandingConfig> = {};

  if (!valuesEqual(draft.logo_url, companyBase.logo_url)) {
    override.logo_url = draft.logo_url;
  }
  if (!valuesEqual(draft.logo_url_dark, companyBase.logo_url_dark)) {
    override.logo_url_dark = draft.logo_url_dark;
  }
  if (!valuesEqual(draft.logo_url_light, companyBase.logo_url_light)) {
    override.logo_url_light = draft.logo_url_light;
  }
  if (!valuesEqual(draft.show_company_name, companyBase.show_company_name)) {
    override.show_company_name = draft.show_company_name;
  }

  const backgroundDiff = diffNestedRecord(companyBase.background, draft.background);
  if (backgroundDiff) {
    override.background = backgroundDiff as BrandingBackground;
  }

  const watermarkDiff = diffNestedRecord(companyBase.watermark, draft.watermark);
  if (watermarkDiff) {
    override.watermark = watermarkDiff as BrandingWatermark;
  }

  return override;
}

/** Collapse legacy per-appearance background values to unified values on save. */
export function normalizeUnifiedBackgroundBranding(
  branding: BrandingConfig,
): BrandingConfig {
  const background = branding.background;
  if (!background) return branding;

  if (background.mode === "color") {
    const color = readUnifiedBackgroundColor(background);
    return mergeBrandingDraft(branding, {
      background: { color_light: color, color_dark: color },
    });
  }

  if (background.mode === "image") {
    const images = readBackgroundImageUrls(branding);
    const url = images.dark ?? images.light;
    if (!url) return branding;
    return mergeBrandingDraft(branding, {
      background: { image_url_dark: url, image_url_light: url },
    });
  }

  return branding;
}

export function mergeWatermarkDraft(
  current: BrandingWatermark | null | undefined,
  patch: Partial<BrandingWatermark> | null | undefined,
): BrandingWatermark {
  const base = {
    enabled: false,
    position: "bottom_right" as const,
    opacity: 100,
    logo_url: null as string | null,
    ...current,
  };

  return {
    ...base,
    ...patch,
    enabled: patch?.enabled ?? base.enabled ?? false,
    logo_url: patch?.logo_url !== undefined ? patch.logo_url : base.logo_url ?? null,
  };
}

export function mergeBrandingDraft(
  base: BrandingConfig | null | undefined,
  patch: Partial<BrandingConfig>,
): BrandingConfig {
  const current = base ?? defaultBrandingConfig();
  return {
    ...current,
    ...patch,
    background: {
      ...defaultBackground(),
      ...current.background,
      ...patch.background,
    },
    watermark: mergeWatermarkDraft(current.watermark, patch.watermark),
  };
}

export function resolveWatermarkLogoUrl(
  branding: BrandingConfig | null | undefined,
): string | null {
  return branding?.watermark?.logo_url ?? null;
}

export function resolveWatermarkOpacity(
  branding: { watermark?: BrandingWatermark } | null | undefined,
): number {
  const opacity = branding?.watermark?.opacity;
  if (opacity == null || Number.isNaN(opacity)) return 100;
  return Math.min(100, Math.max(0, Math.round(opacity)));
}

export function backgroundModeLabel(mode: BrandingBackgroundMode): string {
  switch (mode) {
    case "color":
      return "Solid colour";
    case "image":
      return "Image";
    case "theme":
    default:
      return "Theme gradient";
  }
}
